import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Board } from '@/stores/canvasStore';
import type { LibraryCard } from '@/lib/library/libraryCard';
import {
  buildBoardSpaces,
  buildChildBoardSpaces,
  buildSourceSpaces,
  buildSubjectSpaces,
  findBoardSpace,
  type LibrarySpace,
} from '@/lib/library/collections';
import { SpaceTile } from './SpaceCover';
import { SpaceScreen, type SpaceCloseMode, type SpaceRect } from './SpaceScreen';

export interface SpacesViewProps {
  /** Every card, canvas notes included: boards show all they hold. */
  cards: LibraryCard[];
  /** The feed's cards (no canvas notes): the automatic spaces by source and subject. Defaults to `cards`. */
  feedCards?: LibraryCard[];
  boards: Board[];
  /** The open space's name, or null on the list. The shell header shows it instead of "Spaces". */
  onTitleChange?: (title: string | null) => void;
}

interface OpenSpace {
  key: number;
  id: string;
  /** What the space looked like when opened; used if it empties while open. */
  snapshot: LibrarySpace;
  origin: SpaceRect | null;
  closing: SpaceCloseMode | null;
}

/** The history entry an open space pushes carries its depth here. */
const DEPTH_KEY = 'librarySpaceDepth';

function historyDepth(state: unknown): number {
  const v = (state as Record<string, unknown> | null)?.[DEPTH_KEY];
  return typeof v === 'number' ? v : 0;
}

/** Where the Spaces list was scrolled, kept across tab switches for this page load. */
let savedListScroll = 0;

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="px-4 pb-8">
      <h2 className="px-1 pb-4 font-mono text-[11px] uppercase tracking-[0.08em] text-ink-3">{title}</h2>
      {children}
    </section>
  );
}

function SpaceGrid({ spaces, onOpen }: { spaces: LibrarySpace[]; onOpen: (s: LibrarySpace, front: HTMLElement | null) => void }) {
  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-7">
      {spaces.map((space) => (
        <SpaceTile key={space.id} space={space} onOpen={onOpen} />
      ))}
    </div>
  );
}

/**
 * The Spaces tab: the user's boards, then automatic spaces by source and by
 * subject, each a pile of its own cards. Tapping one grows it into its own
 * grid (SpaceScreen); spaces inside spaces stack. Every open space pushes a
 * history entry, so the back button, a swipe from the left edge, Escape and
 * the browser's back gesture all close the top one the same way. The list
 * stays mounted underneath, so it keeps its scroll position.
 */
export function SpacesView({ cards, feedCards, boards, onTitleChange }: SpacesViewProps) {
  const autoCards = feedCards ?? cards;
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const keyRef = useRef(0);
  const pendingModeRef = useRef<SpaceCloseMode | null>(null);
  const [stack, setStack] = useState<OpenSpace[]>([]);

  const boardSpaces = useMemo(() => buildBoardSpaces(cards, boards), [cards, boards]);
  const sourceSpaces = useMemo(() => buildSourceSpaces(autoCards), [autoCards]);
  const subjectSpaces = useMemo(() => buildSubjectSpaces(autoCards), [autoCards]);

  const live = stack.filter((s) => !s.closing);
  const liveCount = live.length;
  const liveCountRef = useRef(0);
  useEffect(() => {
    liveCountRef.current = liveCount;
  }, [liveCount]);

  /** The space as it is now (cards may have changed since it opened). */
  const resolve = useCallback(
    (entry: OpenSpace): LibrarySpace => {
      const { id } = entry;
      const found = id.startsWith('board:')
        ? findBoardSpace(cards, boards, entry.snapshot.boardId ?? '')
        : (id.startsWith('source:') ? sourceSpaces : subjectSpaces).find((s) => s.id === id);
      return found ?? entry.snapshot;
    },
    [cards, boards, sourceSpaces, subjectSpaces],
  );

  const open = useCallback((space: LibrarySpace, front: HTMLElement | null) => {
    const container = containerRef.current?.getBoundingClientRect();
    const r = front?.getBoundingClientRect();
    const origin = container && r && r.width > 0 ? { x: r.left - container.left, y: r.top - container.top, w: r.width, h: r.height } : null;
    const depth = liveCountRef.current + 1;
    window.history.pushState({ ...(window.history.state ?? {}), [DEPTH_KEY]: depth }, '');
    keyRef.current += 1;
    const entry: OpenSpace = { key: keyRef.current, id: space.id, snapshot: space, origin, closing: null };
    setStack((prev) => [...prev, entry]);
  }, []);

  /** Mark every open space deeper than `depth` as leaving. */
  const closeTo = useCallback((depth: number, mode: SpaceCloseMode) => {
    setStack((prev) => {
      let seen = 0;
      return prev.map((s) => {
        if (s.closing) return s;
        seen += 1;
        return seen > depth ? { ...s, closing: mode } : s;
      });
    });
  }, []);

  /** Close the top space through history, so every way out takes the same path. */
  const requestClose = useCallback(
    (mode: SpaceCloseMode) => {
      const count = liveCountRef.current;
      if (count === 0) return;
      if (historyDepth(window.history.state) === count) {
        pendingModeRef.current = mode;
        window.history.back();
      } else {
        // Our entry is gone (replaced by something else): close without history
        closeTo(count - 1, mode);
      }
    },
    [closeTo],
  );

  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      const depth = historyDepth(e.state);
      const mode = pendingModeRef.current ?? 'back';
      pendingModeRef.current = null;
      if (depth < liveCountRef.current) closeTo(depth, mode);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [closeTo]);

  useEffect(() => {
    if (liveCount === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      // A dialog or sheet on top handles its own Escape
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      requestClose('back');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [liveCount, requestClose]);

  // Leaving the tab with spaces open: drop the entries this view pushed
  useEffect(
    () => () => {
      const depth = historyDepth(window.history.state);
      if (depth > 0) window.history.go(-depth);
    },
    [],
  );

  const removeEntry = useCallback((key: number) => setStack((prev) => prev.filter((s) => s.key !== key)), []);

  // Shell header title
  const topLabel = live.length > 0 ? resolve(live[live.length - 1]).label : null;
  useEffect(() => {
    onTitleChange?.(topLabel);
  }, [topLabel, onTitleChange]);
  useEffect(() => () => onTitleChange?.(null), [onTitleChange]);

  // List scroll memory
  useLayoutEffect(() => {
    if (listRef.current) listRef.current.scrollTop = savedListScroll;
  }, []);

  const covered = stack.length > 0;
  const hasAny = cards.length > 0;

  return (
    <div ref={containerRef} className="relative h-full overflow-hidden">
      <div
        ref={listRef}
        onScroll={(e) => {
          savedListScroll = e.currentTarget.scrollTop;
        }}
        aria-hidden={covered || undefined}
        {...(covered ? { inert: '' } : {})}
        className="h-full overflow-y-auto overscroll-contain pt-3"
        style={{ paddingBottom: 'var(--library-bottom-inset, 24px)', pointerEvents: covered ? 'none' : undefined }}
      >
        {!hasAny ? (
          <div className="flex h-64 flex-col items-center justify-center gap-1 px-8 text-center">
            <p className="text-[15px] text-ink-2">No spaces yet</p>
            <p className="text-[13px] text-ink-3">Save a few things and they sort themselves into spaces here.</p>
          </div>
        ) : (
          <>
            {boardSpaces.length > 0 && (
              <Section title="Your spaces">
                <SpaceGrid spaces={boardSpaces} onOpen={open} />
              </Section>
            )}
            <Section title="By source">
              <SpaceGrid spaces={sourceSpaces} onOpen={open} />
            </Section>
            <Section title="By subject">
              {subjectSpaces.length > 0 ? (
                <SpaceGrid spaces={subjectSpaces} onOpen={open} />
              ) : (
                <p className="px-1 text-[13px] leading-[19px] text-ink-3">Sorting your library by subject. This fills in as items are read.</p>
              )}
            </Section>
          </>
        )}
      </div>

      {stack.map((entry, i) => {
        const space = resolve(entry);
        const parent = i > 0 ? resolve(stack[i - 1]) : null;
        return (
          <SpaceScreen
            key={entry.key}
            space={space}
            childSpaces={space.kind === 'board' && space.boardId ? buildChildBoardSpaces(cards, boards, space.boardId) : []}
            origin={entry.origin}
            closing={entry.closing}
            backLabel={parent ? `Back to ${parent.label}` : 'Back to spaces'}
            onBack={() => requestClose('back')}
            onSwipeClose={() => requestClose('swipe')}
            onExited={() => removeEntry(entry.key)}
            onOpenChild={open}
            zIndex={10 + i}
          />
        );
      })}
    </div>
  );
}
